export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export default async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // As Base64 string
  return croppedCanvas.toDataURL("image/jpeg");

  // As a blob
  // return new Promise((resolve, reject) => {
  //   croppedCanvas.toBlob((file) => {
  //     resolve(URL.createObjectURL(file));
  //   }, "image/jpeg");
  // });
}

export const stripBase64Prefix = (base64String) => {
  if (!base64String) return;
  if (!base64String.includes("data:")) {
    return base64String;
  }
  const base64Regex = /^data:image\/[a-zA-Z+]+;base64,/;
  return base64String.replace(base64Regex, "");
};

export const addBase64Prefix = (base64String, fallbackFormat = "jpeg") => {
  if (!base64String) return;

  if (base64String.startsWith("data:image/")) {
    return base64String;
  }

  // Detect the image type
  const detectedType = detectImageType(base64String);
  const imageType = detectedType || fallbackFormat;

  return `data:image/${imageType};base64,${base64String}`;
};

export const getLatestImage = (record, imageType = "Detail") => {
  if (!record) return null;

  const images = record.images?.filter(
    (img) => img.imageType?.name === imageType,
  );

  if (!images?.length) {
    return null;
  }

  // Use reduce without initial value to avoid null comparison issue
  return images.reduce((prev, curr) => (prev.id > curr.id ? prev : curr));
};

// Function to detect image type from base64 string
const detectImageType = (base64String) => {
  if (!base64String || typeof base64String !== "string") return null;

  const signatures = {
    // Format: [signature bytes, format name]
    png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    jpeg: [0xff, 0xd8, 0xff],
    gif: [0x47, 0x49, 0x46, 0x38], // GIF8
    bmp: [0x42, 0x4d], // BM
    ico: [0x00, 0x00, 0x01, 0x00],
    tiff_le: [0x49, 0x49, 0x2a, 0x00], // Little endian
    tiff_be: [0x4d, 0x4d, 0x00, 0x2a], // Big endian
  };

  try {
    const cleanBase64 = base64String.replace(/^data:image\/[^;]+;base64,/, "");
    const binaryString = atob(cleanBase64.substring(0, 32));
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Check each signature
    for (const [format, signature] of Object.entries(signatures)) {
      if (signature.every((byte, index) => bytes[index] === byte)) {
        return format.replace("_le", "").replace("_be", ""); // Clean up TIFF variants
      }
    }

    // Special cases
    // WebP: RIFF...WEBP
    if (
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return "webp";
    }

    // SVG
    const textStart = binaryString.substring(0, 20).toLowerCase();
    if (textStart.includes("<?xml") || textStart.includes("<svg")) {
      return "svg+xml";
    }

    return null;
  } catch (error) {
    console.warn("Error in advanced image type detection:", error);
    return null;
  }
};
